
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Award, ArrowRight, RefreshCw, Share2, Trophy, TrendingUp, ChartBar, Star } from "lucide-react";
import { useGame } from '../../contexts/GameContext';
import { useNavigate } from 'react-router-dom';
import { toast } from "@/components/ui/sonner";
import { motion } from "framer-motion";
import { Progress } from "@/components/ui/progress";

interface CompletionScreenProps {
  onRestart: () => void;
}

const CompletionScreen: React.FC<CompletionScreenProps> = ({ onRestart }) => {
  const { 
    selectedRole, 
    careerLevel, 
    yearsPassed, 
    experience, 
    achievements, 
    kpis, 
    skills,
    decisionHistory 
  } = useGame();
  
  const navigate = useNavigate();
  const [showStats, setShowStats] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'achievements' | 'kpis' | 'journey'>('overview');
  const [animateProgress, setAnimateProgress] = useState(false);
  
  // Calculate performance scores
  const calculatePerformanceScore = () => {
    const uptimeScore = Math.min(100, kpis.uptime) * 0.25;
    const energyScore = (1 - Math.min(1, kpis.energyCost / 0.1)) * 25;
    const profitScore = Math.min(100, kpis.profitMargin * 2) * 0.25;
    const efficiencyScore = Math.min(100, kpis.efficiency) * 0.25;
    
    return Math.round(uptimeScore + energyScore + profitScore + efficiencyScore);
  };
  
  const performanceScore = calculatePerformanceScore();
  
  // Calculate career score
  const careerScore = Math.round((
    (careerLevel === 'Expert' ? 100 : careerLevel === 'Leader' ? 80 : careerLevel === 'Senior' ? 60 : careerLevel === 'Mid-Level' ? 40 : 20) +
    Math.min(100, experience / 10) +
    Math.min(100, skills.length * 5) +
    Math.min(100, achievements.length * 10)
  ) / 4);
  
  // Animation timing
  useEffect(() => {
    // Delayed animation for statistics
    setTimeout(() => {
      setShowStats(true);
    }, 1000);
    
    setTimeout(() => {
      setAnimateProgress(true);
    }, 1500);
  }, []);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.3
      }
    }
  };
  
  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        type: "spring",
        damping: 12
      }
    }
  };
  
  const handleShare = () => {
    toast("Sharing success!", {
      description: "Your career accomplishments have been shared.",
      icon: <Share2 className="h-4 w-4" />,
    });
  };
  
  if (!selectedRole) return null;
  
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="py-4"
    >
      <Card className="w-full max-w-5xl mx-auto bg-web3-blue/20 border border-web3-green/20 overflow-hidden">
        <CardHeader className="text-center border-b border-web3-green/20 pb-6 relative">
          <motion.div 
            className="absolute top-0 left-0 w-full h-full bg-web3-green/5"
            initial={{ width: 0 }}
            animate={{ width: '100%' }}
            transition={{ duration: 2, ease: "easeInOut" }}
          />
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ 
              type: "spring",
              stiffness: 200,
              damping: 15,
              delay: 0.2
            }}
            className="mx-auto w-24 h-24 rounded-full bg-web3-green/20 flex items-center justify-center mb-5"
          >
            <Trophy className="h-14 w-14 text-web3-green" />
          </motion.div>
          
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <CardTitle className="text-4xl text-web3-green mb-2">Career Completed!</CardTitle>
            <CardDescription className="text-xl text-white/80">
              Congratulations on reaching {selectedRole.careerPath.find(p => p.level === careerLevel)?.title}
            </CardDescription>
          </motion.div>
        </CardHeader>
        
        {/* Tabs navigation */}
        <div className="border-b border-web3-blue/20">
          <div className="flex overflow-x-auto">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`px-5 py-3 font-medium transition-colors ${
                activeTab === 'overview' ? 'text-web3-green border-b-2 border-web3-green' : 'text-white/60 hover:text-white'
              }`}
            >
              Overview
            </button>
            <button 
              onClick={() => setActiveTab('kpis')}
              className={`px-5 py-3 font-medium transition-colors ${
                activeTab === 'kpis' ? 'text-web3-green border-b-2 border-web3-green' : 'text-white/60 hover:text-white'
              }`}
            >
              Performance
            </button>
            <button 
              onClick={() => setActiveTab('achievements')}
              className={`px-5 py-3 font-medium transition-colors ${
                activeTab === 'achievements' ? 'text-web3-green border-b-2 border-web3-green' : 'text-white/60 hover:text-white'
              }`}
            >
              Achievements
            </button>
            <button 
              onClick={() => setActiveTab('journey')}
              className={`px-5 py-3 font-medium transition-colors ${
                activeTab === 'journey' ? 'text-web3-green border-b-2 border-web3-green' : 'text-white/60 hover:text-white'
              }`}
            >
              Journey
            </button>
          </div>
        </div>
        
        <CardContent className="pt-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              {/* Career Score */}
              <motion.div 
                className="text-center py-6"
                variants={cardVariants}
              >
                <div className="inline-block rounded-full bg-web3-green/10 border border-web3-green/20 p-8 mb-4">
                  <div className="text-5xl font-bold text-web3-green">{careerScore}</div>
                  <div className="text-white/60 mt-1">Career Score</div>
                </div>
                
                <p className="text-white/80 max-w-md mx-auto">
                  Your career score reflects your overall success in the Bitcoin mining industry.
                </p>
              </motion.div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <motion.div 
                  className="p-4 bg-web3-blue/20 rounded-lg text-center transform transition-all duration-300 hover:scale-105 hover:bg-web3-blue/30"
                  variants={cardVariants}
                >
                  <p className="text-xs text-white/60 mb-1">Final Title</p>
                  <p className="font-bold text-white text-lg">{selectedRole.careerPath.find(p => p.level === careerLevel)?.title}</p>
                </motion.div>
                
                <motion.div 
                  className="p-4 bg-web3-blue/20 rounded-lg text-center transform transition-all duration-300 hover:scale-105 hover:bg-web3-blue/30"
                  variants={cardVariants}
                >
                  <p className="text-xs text-white/60 mb-1">Career Length</p>
                  <p className="font-bold text-white text-lg">{yearsPassed} Years</p>
                </motion.div>
                
                <motion.div 
                  className="p-4 bg-web3-blue/20 rounded-lg text-center transform transition-all duration-300 hover:scale-105 hover:bg-web3-blue/30"
                  variants={cardVariants}
                >
                  <p className="text-xs text-white/60 mb-1">Experience</p>
                  <p className="font-bold text-white text-lg">{experience} XP</p>
                </motion.div>
                
                <motion.div 
                  className="p-4 bg-web3-blue/20 rounded-lg text-center transform transition-all duration-300 hover:scale-105 hover:bg-web3-blue/30"
                  variants={cardVariants}
                >
                  <p className="text-xs text-white/60 mb-1">Skills Mastered</p>
                  <p className="font-bold text-white text-lg">{skills.length}</p>
                </motion.div>
              </div>
              
              <motion.div
                className="mt-6 bg-web3-green/10 rounded-lg p-6 border border-web3-green/20"
                variants={cardVariants}
              >
                <h3 className="text-xl font-medium text-web3-green mb-4">Career Summary</h3>
                <p className="text-white/90 leading-relaxed">
                  You've had an impressive career in the Bitcoin mining industry, reaching the position of {selectedRole.careerPath.find(p => p.level === careerLevel)?.title}. 
                  Your journey spanned {yearsPassed} years, during which you acquired {skills.length} valuable skills and achieved a performance score of {performanceScore}.
                  {careerScore >= 80 && " Your exceptional leadership and technical expertise have made you a respected figure in the industry."}
                  {careerScore >= 60 && careerScore < 80 && " Your solid performance and consistent growth have established you as a valuable professional."}
                  {careerScore < 60 && " While facing challenges, you've gained valuable experience that will serve you well in future endeavors."}
                </p>
              </motion.div>
            </motion.div>
          )}
          
          {/* KPIs Tab */}
          {activeTab === 'kpis' && (
            <motion.div
              key="kpis"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <div className="inline-block rounded-full bg-bitcoin-accent/10 border border-bitcoin-accent/20 p-8 mb-4">
                  <div className="text-5xl font-bold text-bitcoin-accent">{performanceScore}</div>
                  <div className="text-white/60 mt-1">Performance Score</div>
                </div>
              </div>
              
              <div className="space-y-5">
                <div className="bg-web3-blue/10 rounded-lg p-5">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2 text-bitcoin-accent" />
                      <h4 className="text-lg font-medium text-white">Uptime</h4>
                    </div>
                    <span className="text-xl font-bold text-bitcoin-accent">{kpis.uptime.toFixed(1)}%</span>
                  </div>
                  <Progress value={animateProgress ? kpis.uptime : 0} className="h-3" />
                  <div className="mt-2 text-sm text-white/70">
                    {kpis.uptime >= 95 ? "Exceptional uptime performance!" : 
                     kpis.uptime >= 85 ? "Good uptime maintenance." : 
                     "Room for improvement in system reliability."}
                  </div>
                </div>
                
                <div className="bg-web3-blue/10 rounded-lg p-5">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <ChartBar className="h-5 w-5 mr-2 text-bitcoin-accent" />
                      <h4 className="text-lg font-medium text-white">Energy Cost</h4>
                    </div>
                    <span className="text-xl font-bold text-bitcoin-accent">${kpis.energyCost.toFixed(3)}/kWh</span>
                  </div>
                  <Progress value={animateProgress ? (1 - kpis.energyCost / 0.1) * 100 : 0} className="h-3" />
                  <div className="mt-2 text-sm text-white/70">
                    {kpis.energyCost <= 0.05 ? "Impressively low energy costs!" : 
                     kpis.energyCost <= 0.07 ? "Competitive energy pricing." : 
                     "Higher than optimal energy costs."}
                  </div>
                </div>
                
                <div className="bg-web3-blue/10 rounded-lg p-5">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2 text-bitcoin-accent" />
                      <h4 className="text-lg font-medium text-white">Profit Margin</h4>
                    </div>
                    <span className="text-xl font-bold text-bitcoin-accent">{kpis.profitMargin.toFixed(1)}%</span>
                  </div>
                  <Progress value={animateProgress ? Math.min(100, kpis.profitMargin * 2) : 0} className="h-3" />
                  <div className="mt-2 text-sm text-white/70">
                    {kpis.profitMargin >= 30 ? "Outstanding profit performance!" : 
                     kpis.profitMargin >= 20 ? "Healthy profit margins." : 
                     "Slim profit margins - consider cost optimization."}
                  </div>
                </div>
                
                <div className="bg-web3-blue/10 rounded-lg p-5">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <ChartBar className="h-5 w-5 mr-2 text-bitcoin-accent" />
                      <h4 className="text-lg font-medium text-white">Efficiency</h4>
                    </div>
                    <span className="text-xl font-bold text-bitcoin-accent">{kpis.efficiency.toFixed(1)}%</span>
                  </div>
                  <Progress value={animateProgress ? kpis.efficiency : 0} className="h-3" />
                  <div className="mt-2 text-sm text-white/70">
                    {kpis.efficiency >= 85 ? "Remarkably efficient operations!" : 
                     kpis.efficiency >= 75 ? "Good operational efficiency." : 
                     "Efficiency improvements needed."}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Achievements Tab */}
          {activeTab === 'achievements' && (
            <motion.div
              key="achievements"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center">
                  <Award className="h-8 w-8 text-yellow-300 mr-2" />
                  <h3 className="text-xl font-medium text-white">
                    {achievements.length} {achievements.length === 1 ? 'Achievement' : 'Achievements'} Unlocked
                  </h3>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {achievements.map((achievement, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-web3-blue/20 border border-web3-blue/30 rounded-md p-4 flex items-start"
                  >
                    <div className="bg-yellow-500/20 rounded-full p-2 mr-3">
                      <Star className="h-5 w-5 text-yellow-300" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{achievement}</p>
                      <p className="text-white/60 text-sm">
                        {achievement.includes("Promoted") ? "Career advancement" : 
                         achievement.includes("Completed") ? "Major milestone" :
                         achievement.includes("Unlocked") ? "New capability" :
                         achievement.includes("Optimized") ? "Performance improvement" :
                         "Notable accomplishment"}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {achievements.length === 0 && (
                <div className="text-center py-10 text-white/60">
                  No achievements recorded during your career.
                </div>
              )}
            </motion.div>
          )}
          
          {/* Journey Tab */}
          {activeTab === 'journey' && (
            <motion.div
              key="journey"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center">
                  <ChartBar className="h-8 w-8 text-web3-green mr-2" />
                  <h3 className="text-xl font-medium text-white">
                    Your {yearsPassed} Year Journey
                  </h3>
                </div>
              </div>
              
              <div className="relative pl-8 border-l-2 border-dashed border-web3-blue/30 space-y-8 max-h-[400px] overflow-y-auto pr-4 py-2">
                {decisionHistory.length > 0 ? decisionHistory.map((decision, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    className="relative"
                  >
                    <div className="absolute -left-11 bg-web3-blue/30 rounded-full p-1">
                      <div className="h-5 w-5 rounded-full bg-web3-blue/60 flex items-center justify-center">
                        <span className="text-xs text-white">{index + 1}</span>
                      </div>
                    </div>
                    <div className="bg-web3-blue/10 rounded-lg p-4 border border-web3-blue/20">
                      <p className="text-white/90 mb-2">{decision.decision}</p>
                      <p className="text-white/70 text-sm">{decision.outcome}</p>
                      <p className="text-white/50 text-xs mt-2">Year {Math.floor(index / 3) + 1}</p>
                    </div>
                  </motion.div>
                )) : (
                  <div className="text-center py-10 text-white/60">
                    No decision history recorded.
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </CardContent>
        
        <CardFooter className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-web3-green/20">
          <Button 
            onClick={onRestart} 
            className="w-full sm:w-auto hover:scale-105 transition-transform bg-web3-green hover:bg-web3-green/80"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Start New Career
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate('/')} 
            className="w-full sm:w-auto hover:scale-105 transition-transform"
          >
            <ArrowRight className="mr-2 h-4 w-4" />
            Return to Home
          </Button>
          <Button 
            variant="outline" 
            onClick={handleShare} 
            className="w-full sm:w-auto hover:scale-105 transition-transform"
          >
            <Share2 className="mr-2 h-4 w-4" />
            Share Results
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default CompletionScreen;
